import React, { useRef, MutableRefObject } from 'react';
import { Flex, FlexBehavior, Tooltip } from 'react-magma-dom';
import { useTooltipForLongName } from '../../hooks/useLongNameTooltip';
import { Modal } from './Modal';
import { ModalButton } from '../Button';
import {
  MarginWrapper,
  StyledModalFooter,
  StyledModalLinkButton,
  StyledModalText,
  StyledExemplarName,
} from '../Styled';
import { COLORS } from '../../constants';
import { getStageColors } from '../../utils';

interface ILeaveEditingModal {
  stage: string;
  isSavedExemplar: boolean;
  exemplarName?: string;
  isTextEditor?: boolean;
  onClose: () => void;
  onConfirmLeaving: () => void;
  onSave: () => void;
  onUpdate: () => void;
}

export const LeaveEditingModal: React.FC<ILeaveEditingModal> = (props): JSX.Element => {
  const { stage, isSavedExemplar, exemplarName, isTextEditor, onClose, onConfirmLeaving, onSave, onUpdate } = props;

  const styledModalTextRef = useRef() as MutableRefObject<HTMLParagraphElement>;
  const exemplarNameRef = useRef() as MutableRefObject<HTMLSpanElement>;

  const isExemplarNameTooltip = useTooltipForLongName(styledModalTextRef, exemplarNameRef);

  const { main: mainColor, light: highlightColor } = getStageColors(stage);

  const showExemplarName = (withTooltip: boolean) => {
    const defaultExemplarNameElement = (
      <StyledExemplarName ref={exemplarNameRef} highlightColor={highlightColor}>
        <strong>{exemplarName}</strong>
      </StyledExemplarName>
    );

    const tooltipStyle = {
      minWidth: 'fit-content',
    };

    return withTooltip ? (
      <Tooltip testId="longname-modal-tooltip" tooltipStyle={tooltipStyle} content={exemplarName}>
        {defaultExemplarNameElement}
      </Tooltip>
    ) : (
      defaultExemplarNameElement
    );
  };

  const renderText = () =>
    isSavedExemplar ? (
      <>
        Before you leave the page would you like to save changes made to <br />
        {showExemplarName(isExemplarNameTooltip)} <span>?</span>
      </>
    ) : isTextEditor ? (
      <>If you leave this page without saving, your changes will be lost</>
    ) : (
      <>Would you like to save your changes as a new text?</>
    );

  const confirmBtnText = isTextEditor
    ? 'Return to editing'
    : isSavedExemplar
    ? 'Save and Continue'
    : 'Save as new text';
  const confirmBtnHandler = isSavedExemplar ? onUpdate : onSave;

  return (
    <Modal onCloseClick={onClose} headerText="You’ve made changes">
      <MarginWrapper margin="-8px 0px 0px 0px">
        <StyledModalText ref={styledModalTextRef}>{renderText()}</StyledModalText>
      </MarginWrapper>
      <MarginWrapper margin="32px 0px 0px 0px">
        <Flex behavior={FlexBehavior.container}>
          <ModalButton
            bGColor={COLORS.white}
            borderColor={mainColor}
            color={mainColor}
            text="Continue without saving"
            onButtonClick={onConfirmLeaving}
          />
          <ModalButton
            bGColor={mainColor}
            color={COLORS.white}
            text={confirmBtnText}
            onButtonClick={confirmBtnHandler}
          />
        </Flex>
        {isSavedExemplar && (
          <StyledModalFooter>
            or
            <StyledModalLinkButton textColor={mainColor} onClick={onSave}>
              Save as new text
            </StyledModalLinkButton>
          </StyledModalFooter>
        )}
      </MarginWrapper>
    </Modal>
  );
};
